"use client"

import { animate } from "framer-motion"
import { useLayoutEffect, useRef } from "react"

function mod(n: number, m: number) {
  return ((n % m) + m) % m
}

function ScrollingDigit({
  value,
  trend = 1,
}: {
  value: number
  trend?: number
}) {
  const outerRef = useRef<HTMLSpanElement>(null)
  const columnRef = useRef<HTMLSpanElement>(null)
  const prevValue = useRef(value)

  useLayoutEffect(() => {
    const column = columnRef.current
    if (!column || value === prevValue.current) {
      return
    }

    const oldValue = prevValue.current
    let delta = value - oldValue

    // Force scroll direction when wrapping 9→0 or 0→9
    if (trend > 0 && value < oldValue) {
      delta = 10 - oldValue + value
    } else if (trend < 0 && value > oldValue) {
      delta = value - 10 - oldValue
    }

    const digitHeight =
      column.querySelector("[data-digit]")?.getBoundingClientRect().height ?? 0

    const controls = animate(
      column,
      { y: [delta * digitHeight, 0] },
      { type: "spring", duration: 0.9, bounce: 0 }
    )

    prevValue.current = value
    return () => controls.stop()
  }, [trend, value])

  const above = Array.from({ length: 9 }, (_, offset) =>
    mod(value - (9 - offset), 10)
  )
  const below = Array.from({ length: 9 }, (_, offset) =>
    mod(value + offset + 1, 10)
  )

  return (
    <span
      ref={outerRef}
      className="relative inline-flex justify-center overflow-hidden"
      style={{ height: "1em", lineHeight: 1 }}
    >
      <span
        ref={columnRef}
        className="relative inline-flex flex-col items-center justify-center"
      >
        <span className="absolute bottom-full left-0 flex w-full flex-col items-center">
          {above.map((digit) => (
            <span key={`a-${digit}`} data-digit className="block">
              {digit}
            </span>
          ))}
        </span>
        <span data-digit className="block">
          {value}
        </span>
        <span className="absolute top-full left-0 flex w-full flex-col items-center">
          {below.map((digit) => (
            <span key={`b-${digit}`} data-digit className="block">
              {digit}
            </span>
          ))}
        </span>
      </span>
    </span>
  )
}

type ScrollingNumberProps = {
  value: number
  digits?: number
  prefix?: string
  className?: string
  /** 1 = always scroll up, -1 = always down, 0 = shortest path */
  trend?: number
}

const ScrollingNumber = ({
  value,
  digits = 0,
  prefix = "",
  className,
  trend = 1,
}: ScrollingNumberProps) => {
  const formatted = value.toLocaleString("en-US", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })

  return (
    <span
      className={className}
      style={{ display: "inline-flex", lineHeight: 1, whiteSpace: "nowrap" }}
    >
      <span className="sr-only">{`${prefix}${formatted}`}</span>
      {prefix ? <span aria-hidden>{prefix}</span> : null}
      <span aria-hidden className="inline-flex">
        {[...formatted].map((char, index) => {
          if (char >= "0" && char <= "9") {
            return (
              <ScrollingDigit
                // Position-stable key so reels persist across value changes
                key={`d-${index}-${formatted.length}`}
                value={Number(char)}
                trend={trend}
              />
            )
          }

          return (
            <span key={`s-${index}-${char}`} className="inline-block">
              {char}
            </span>
          )
        })}
      </span>
    </span>
  )
}

export default ScrollingNumber
